import { t } from "@lingui/macro";
import {
	createServiceTag,
	db,
	deleteServiceTag,
	updateServiceTag,
} from "@repo/ui";
import { BellRing, Edit, Plus, Tags, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useServiceTags } from "../hooks/useServiceTags";
import type {
	Service,
	ServiceNotificationSettings,
	ServiceTag,
	ServiceTagColor,
} from "../types";
import { isAdminRole } from "../utils/auth";
import {
	getServiceTagClassName,
	isTagDeleted,
	SERVICE_TAG_COLORS,
} from "../utils/serviceTags";
import { getContentFieldProps } from "../utils/textDirection";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

const DEFAULT_FROM_NAME = "{{ service.name }} - Agent";
const DEFAULT_SUBJECT =
	"New call from {{ callerNumber }} about {{ callSubject }}";
const DEFAULT_BODY =
	"A new call from {{ callerNumber }} about {{ callSubject }} was just handled.\n\n{{ linkToCall }}";

function normalizeNotifications(
	notifications: Service["notifications"],
): ServiceNotificationSettings {
	return {
		enabled: notifications?.enabled ?? true,
		emails: Array.isArray(notifications?.emails)
			? [...notifications.emails]
			: [],
		fromName: notifications?.fromName || DEFAULT_FROM_NAME,
		subject: notifications?.subject || DEFAULT_SUBJECT,
		body: notifications?.body || DEFAULT_BODY,
	};
}

export function CallResultsSettingsPanel() {
	const {
		user,
		language,
		services: availableServices,
		updateService,
	} = useData();
	const services = useMemo(() => availableServices ?? [], [availableServices]);
	const canManage = isAdminRole(user?.role);
	const isRtl = language === "he";
	const contentFieldProps = (
		value: string | null | undefined,
		extraClassName = "",
	) => getContentFieldProps(value, isRtl ? "rtl" : "ltr", extraClassName);
	const [selectedServiceId, setSelectedServiceId] = useState("");
	const selectedService = useMemo(
		() => services.find((service) => service.id === selectedServiceId) ?? null,
		[services, selectedServiceId],
	);
	const serviceTags = useServiceTags(
		user?.companyId,
		selectedServiceId || null,
	);
	const activeTags = serviceTags.filter((tag) => !isTagDeleted(tag));
	const [notifications, setNotifications] =
		useState<ServiceNotificationSettings>(normalizeNotifications(undefined));
	const [isSavingNotifications, setIsSavingNotifications] = useState(false);
	const [tagEditorOpen, setTagEditorOpen] = useState(false);
	const [editingTagId, setEditingTagId] = useState<string | null>(null);
	const [tagLabel, setTagLabel] = useState("");
	const [tagDescription, setTagDescription] = useState("");
	const [tagColor, setTagColor] = useState<ServiceTagColor>("blue");
	const [isSavingTag, setIsSavingTag] = useState(false);

	useEffect(() => {
		if (!services.length) {
			setSelectedServiceId("");
			return;
		}
		if (!services.some((service) => service.id === selectedServiceId)) {
			setSelectedServiceId(services[0].id || "");
		}
	}, [services, selectedServiceId]);

	useEffect(() => {
		setNotifications(normalizeNotifications(selectedService?.notifications));
		setTagEditorOpen(false);
		setEditingTagId(null);
	}, [selectedService]);

	const resetTagEditor = () => {
		setTagEditorOpen(false);
		setEditingTagId(null);
		setTagLabel("");
		setTagDescription("");
		setTagColor("blue");
	};

	const openNewTag = () => {
		setEditingTagId(null);
		setTagLabel("");
		setTagDescription("");
		setTagColor("blue");
		setTagEditorOpen(true);
	};

	const openEditTag = (tag: ServiceTag) => {
		setEditingTagId(tag.id);
		setTagLabel(tag.label);
		setTagDescription(tag.description || "");
		setTagColor(tag.color);
		setTagEditorOpen(true);
	};

	const saveTag = async () => {
		if (!user?.companyId || !selectedServiceId || !tagLabel.trim()) return;
		const normalizedLabel = tagLabel.trim().toLocaleLowerCase();
		if (
			activeTags.some(
				(tag) =>
					tag.id !== editingTagId &&
					tag.label.trim().toLocaleLowerCase() === normalizedLabel,
			)
		) {
			toast.error(
				t({
					id: "serviceEditor.tags.duplicate",
					message: "Active tag labels must be unique",
				}),
			);
			return;
		}

		setIsSavingTag(true);
		try {
			const payload = {
				label: tagLabel.trim(),
				description: tagDescription.trim(),
				color: tagColor,
			};
			if (editingTagId) {
				await updateServiceTag(
					db,
					user.companyId,
					selectedServiceId,
					editingTagId,
					payload,
				);
				toast.success(
					t({ id: "serviceEditor.tags.updated", message: "Tag updated" }),
				);
			} else {
				await createServiceTag(db, user.companyId, selectedServiceId, payload);
				toast.success(
					t({ id: "serviceEditor.tags.created", message: "Tag created" }),
				);
			}
			resetTagEditor();
		} catch (error) {
			console.error("Failed to save service tag", error);
			toast.error(
				t({
					id: "serviceEditor.tags.saveFailed",
					message: "Failed to save tag",
				}),
			);
		} finally {
			setIsSavingTag(false);
		}
	};

	const deleteTag = async (tag: ServiceTag) => {
		if (!user?.companyId || !selectedServiceId) return;
		try {
			await deleteServiceTag(db, user.companyId, selectedServiceId, tag.id);
		} catch (error) {
			console.error("Failed to delete service tag", error);
			toast.error(
				t({
					id: "serviceEditor.tags.deleteFailed",
					message: "Failed to delete tag",
				}),
			);
		}
	};

	const saveNotifications = async () => {
		if (!selectedService) return;
		setIsSavingNotifications(true);
		try {
			await updateService({
				...selectedService,
				notifications: {
					...notifications,
					emails: notifications.emails
						.map((email) => email.trim())
						.filter(Boolean),
					fromName: notifications.fromName.trim(),
					subject: notifications.subject.trim(),
					body: notifications.body.trim(),
				},
			});
			toast.success(
				t({
					id: "callResults.notificationsSaved",
					message: "Call notification settings saved",
				}),
			);
		} catch (error) {
			console.error("Failed to save call notifications", error);
			toast.error(
				t({
					id: "callResults.notificationsSaveFailed",
					message: "Call notification settings could not be saved.",
				}),
			);
		} finally {
			setIsSavingNotifications(false);
		}
	};

	return (
		<CollapsibleSettingsPanel
			icon={<BellRing className="h-5 w-5" />}
			title={t({
				id: "callResults.title",
				message: "Call results & notifications",
			})}
			description={
				canManage
					? t({
							id: "callResults.description",
							message:
								"Configure call labels and post-call email delivery by service.",
						})
					: t({
							id: "callResults.readOnlyDescription",
							message: "View the call labels available for each service.",
						})
			}
		>
			{services.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					{t({
						id: "callResults.noServices",
						message: "Create a service before configuring call results.",
					})}
				</p>
			) : (
				<div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
					<div className="grid gap-2">
						<Label htmlFor="call-results-service">
							{t({ id: "service", message: "Service" })}
						</Label>
						<Select
							value={selectedServiceId}
							onValueChange={setSelectedServiceId}
						>
							<SelectTrigger id="call-results-service" className="max-w-md">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{services.map((service) => (
									<SelectItem key={service.id} value={service.id || ""}>
										{service.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<section
						className="space-y-4 border-t pt-5"
						aria-labelledby="call-tags-title"
					>
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div>
								<h3
									id="call-tags-title"
									className="flex items-center gap-2 font-medium text-foreground"
								>
									<Tags className="h-4 w-4" />
									{t({ id: "callResults.tagsTitle", message: "Call labels" })}
								</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									{t({
										id: "callResults.tagsDescription",
										message:
											"Used to categorize subsequently processed calls and dashboard results.",
									})}
								</p>
							</div>
							{canManage ? (
								<Button
									type="button"
									size="sm"
									variant="outline"
									onClick={openNewTag}
								>
									<Plus className="me-2 h-4 w-4" />
									{t({ id: "serviceEditor.tags.add", message: "Add tag" })}
								</Button>
							) : null}
						</div>

						{canManage && tagEditorOpen ? (
							<div className="grid gap-3 rounded-lg border-[1.5px] border-card-border shadow-card bg-secondary p-4 sm:grid-cols-2">
								<div className="grid gap-2">
									<Label htmlFor="call-tag-label">
										{t({
											id: "serviceEditor.tags.label",
											message: "Tag label",
										})}
									</Label>
									<Input
										id="call-tag-label"
										value={tagLabel}
										onChange={(event) => setTagLabel(event.target.value)}
										{...contentFieldProps(tagLabel)}
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="call-tag-color">
										{t({ id: "serviceEditor.tags.color", message: "Color" })}
									</Label>
									<Select
										value={tagColor}
										onValueChange={(value) =>
											setTagColor(value as ServiceTagColor)
										}
									>
										<SelectTrigger id="call-tag-color">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{SERVICE_TAG_COLORS.map((color) => (
												<SelectItem key={color} value={color}>
													{color}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="grid gap-2 sm:col-span-2">
									<Label htmlFor="call-tag-description">
										{t({ id: "description", message: "Description" })}
									</Label>
									<Textarea
										id="call-tag-description"
										rows={3}
										value={tagDescription}
										onChange={(event) => setTagDescription(event.target.value)}
										{...contentFieldProps(tagDescription)}
									/>
								</div>
								<div className="flex justify-end gap-2 sm:col-span-2">
									<Button
										type="button"
										variant="ghost"
										onClick={resetTagEditor}
									>
										{t({ id: "cancel", message: "Cancel" })}
									</Button>
									<Button
										type="button"
										onClick={saveTag}
										disabled={isSavingTag || !tagLabel.trim()}
									>
										{isSavingTag
											? t({ id: "saving", message: "Saving..." })
											: t({ id: "save", message: "Save" })}
									</Button>
								</div>
							</div>
						) : null}

						<div className="divide-y rounded-lg border">
							{activeTags.length === 0 ? (
								<p className="p-4 text-sm text-muted-foreground">
									{t({
										id: "serviceEditor.tags.none",
										message: "No active tags yet.",
									})}
								</p>
							) : (
								activeTags.map((tag) => (
									<div
										key={tag.id}
										className="flex items-start justify-between gap-3 p-3"
									>
										<div className="min-w-0">
											<Badge className={getServiceTagClassName(tag.color)}>
												{tag.label}
											</Badge>
											{tag.description ? (
												<p className="mt-1 break-words text-sm text-muted-foreground">
													{tag.description}
												</p>
											) : null}
										</div>
										{canManage ? (
											<div className="flex shrink-0 gap-1">
												<Button
													type="button"
													size="icon"
													variant="ghost"
													onClick={() => openEditTag(tag)}
													aria-label={t({ id: "edit", message: "Edit" })}
												>
													<Edit className="h-4 w-4" />
												</Button>
												<Button
													type="button"
													size="icon"
													variant="ghost"
													onClick={() => deleteTag(tag)}
													aria-label={t({ id: "delete", message: "Delete" })}
												>
													<Trash2 className="h-4 w-4 text-danger-ink" />
												</Button>
											</div>
										) : null}
									</div>
								))
							)}
						</div>
					</section>

					{canManage ? (
						<section
							className="space-y-4 border-t pt-5"
							aria-labelledby="call-notifications-title"
						>
							<div className="flex items-start justify-between gap-4">
								<div>
									<h3
										id="call-notifications-title"
										className="font-medium text-foreground"
									>
										{t({
											id: "callResults.notificationsTitle",
											message: "Email notifications",
										})}
									</h3>
									<p className="mt-1 text-sm text-muted-foreground">
										{t({
											id: "callResults.notificationsDescription",
											message:
												"Send an email after this service handles a call.",
										})}
									</p>
								</div>
								<Switch
									checked={notifications.enabled}
									onCheckedChange={(enabled) =>
										setNotifications((current) => ({ ...current, enabled }))
									}
									aria-label={t({
										id: "serviceEditor.notifications.enabled",
										message: "Notifications Enabled",
									})}
								/>
							</div>
							<div className="grid gap-4 sm:grid-cols-2">
								<div className="grid gap-2 sm:col-span-2">
									<Label>
										{t({
											id: "serviceEditor.notifications.emails",
											message: "Notification emails",
										})}
									</Label>
									<div className="space-y-2">
										{notifications.emails.map((email, index) => (
											<div key={index} className="flex gap-2">
												<Input
													type="email"
													dir="ltr"
													value={email}
													onChange={(event) =>
														setNotifications((current) => ({
															...current,
															emails: current.emails.map((item, itemIndex) =>
																itemIndex === index ? event.target.value : item,
															),
														}))
													}
												/>
												<Button
													type="button"
													size="icon"
													variant="ghost"
													onClick={() =>
														setNotifications((current) => ({
															...current,
															emails: current.emails.filter(
																(_, itemIndex) => itemIndex !== index,
															),
														}))
													}
													aria-label={t({ id: "remove", message: "Remove" })}
												>
													<X className="h-4 w-4" />
												</Button>
											</div>
										))}
									</div>
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="w-fit"
										onClick={() =>
											setNotifications((current) => ({
												...current,
												emails: [...current.emails, ""],
											}))
										}
									>
										<Plus className="me-2 h-4 w-4" />
										{t({
											id: "serviceEditor.notifications.addEmail",
											message: "Add email",
										})}
									</Button>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="notification-from-name">
										{t({
											id: "serviceEditor.notifications.fromName",
											message: "From name",
										})}
									</Label>
									<Input
										id="notification-from-name"
										value={notifications.fromName}
										onChange={(event) =>
											setNotifications((current) => ({
												...current,
												fromName: event.target.value,
											}))
										}
										{...contentFieldProps(notifications.fromName)}
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="notification-subject">
										{t({
											id: "serviceEditor.notifications.subject",
											message: "Notification Subject",
										})}
									</Label>
									<Input
										id="notification-subject"
										value={notifications.subject}
										onChange={(event) =>
											setNotifications((current) => ({
												...current,
												subject: event.target.value,
											}))
										}
										{...contentFieldProps(notifications.subject)}
									/>
								</div>
								<div className="grid gap-2 sm:col-span-2">
									<Label htmlFor="notification-body">
										{t({
											id: "serviceEditor.notifications.body",
											message: "Notification Body",
										})}
									</Label>
									<Textarea
										id="notification-body"
										rows={6}
										value={notifications.body}
										onChange={(event) =>
											setNotifications((current) => ({
												...current,
												body: event.target.value,
											}))
										}
										{...contentFieldProps(notifications.body)}
									/>
								</div>
							</div>
							<div className="flex flex-wrap items-center justify-between gap-3">
								<p className="text-xs text-foreground">
									{t({
										id: "callResults.appliesImmediately",
										message: "Applies immediately to future call processing.",
									})}
								</p>
								<Button
									type="button"
									onClick={saveNotifications}
									disabled={isSavingNotifications}
								>
									{isSavingNotifications
										? t({ id: "saving", message: "Saving..." })
										: t({ id: "saveChanges", message: "Save Changes" })}
								</Button>
							</div>
						</section>
					) : null}
				</div>
			)}
		</CollapsibleSettingsPanel>
	);
}
